import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { SITE } from "@/hooks/useSeo";

/** "/dashboard/goods/3/edit" -> "Edit Goods"; "/merchant/orders" -> "Orders". */
function titleFromPath(pathname: string): string {
  const segs = pathname.split("/").filter(Boolean);
  while (segs.length > 1 && /^\d+$/.test(segs[segs.length - 1])) segs.pop();
  const cap = (s: string) =>
    s.split("-").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
  const last = segs[segs.length - 1] || "dashboard";
  if ((last === "new" || last === "edit") && segs.length >= 2) {
    return `${cap(last)} ${cap(segs[segs.length - 2])}`;
  }
  return cap(last);
}

type Ctx = {
  mounted: boolean;
  title: string;
  setTitle: (t: string) => void;
  activeSection: string | undefined;
  setActiveSection: (s: string | undefined) => void;
};

const LayoutCtx = createContext<Ctx>({
  mounted: false,
  title: "",
  setTitle: () => {},
  activeSection: undefined,
  setActiveSection: () => {},
});

export function LayoutContextProvider({
  children,
  defaultTitle,
  defaultActiveSection,
}: {
  children: ReactNode;
  defaultTitle?: string;
  defaultActiveSection?: string;
}) {
  const [title, setTitle] = useState(defaultTitle || "");
  const [activeSection, setActiveSection] = useState<string | undefined>(defaultActiveSection);
  // Keep the browser-tab title in sync with the dashboard header title.
  // Without this, whatever public page set document.title last (usually
  // "Sign In") sticks for every dashboard tab. Pages that never call
  // useLayoutSync get a title derived from the route path.
  const location = useLocation();
  useEffect(() => {
    document.title = `${title || titleFromPath(location.pathname)} | ${SITE.name}`;
  }, [title, location.pathname]);
  return (
    <LayoutCtx.Provider value={{ mounted: true, title, setTitle, activeSection, setActiveSection }}>
      {children}
    </LayoutCtx.Provider>
  );
}

export const useLayoutCtx = () => useContext(LayoutCtx);

/** Pages wrapped in a layout route call this implicitly via their layout
 *  component's title prop — but it's also exported so a page can set the
 *  title without re-importing the layout. */
export function useLayoutSync(title?: string, activeSection?: string) {
  const { mounted, setTitle, setActiveSection } = useLayoutCtx();
  useEffect(() => {
    if (!mounted) return;
    if (title !== undefined) setTitle(title);
    if (activeSection !== undefined) setActiveSection(activeSection);
    return () => {
      // Reset on unmount so the next page doesn't inherit a stale title.
      if (title !== undefined) setTitle("");
    };
  }, [mounted, title, activeSection, setTitle, setActiveSection]);
}
